// pages/FormViewerPage/sections/PaymentMethodSection.tsx — Payment method radio list
// Metode Duitku tampil SATUAN persis pola digital (1 baris radio per metode, bukan
// kotak bersarang). Baris duitku muncul hanya jika gateway siap (fetchGatewayStatus)
// — customer tidak pernah melihat metode yang tidak bisa dipakai.
import React, { useEffect } from 'react';
import { isNoCodZoneProvince } from '../../../data/adZones';
import { isCodBlockedByShipping, resolveCodShippingLimit } from '../../../constants/codRules';
import QRIcon from '../../../components/icons/QRIcon';
import BankTransferIcon from '../../../components/icons/BankTransferIcon';
import { duitkuLogoFor } from '../../../components/icons/paymentLogos';
import { DUITKU_FORM_METHODS, isKnownDuitkuMethod } from '../../../constants/duitkuFormMethods';
import { useDuitkuReady } from './PaymentMethodSection/useDuitkuReady';
import { isSederhanaMode } from '../../../utils/simpleMode';
import { ms } from '../../../locales/ms';
import { DuitkuRows } from './PaymentMethodSection/DuitkuRows';
import { StandardRow } from './PaymentMethodSection/StandardRow';

const duitkuIconFor = (method: string) =>
    duitkuLogoFor(method)
    ?? (/virtual account/i.test(method) ? BankTransferIcon : QRIcon);

export const PaymentMethodSection: React.FC<{ hook: any; blockSpacingClass: string; subtotal: number }> = ({ hook, blockSpacingClass, subtotal }) => {
    const { form, sortedPaymentKeys, selectedPaymentKey, setSelectedPaymentKey, selectedDuitkuMethod, setSelectedDuitkuMethod, shippingCost, quantity } = hook;
    const lineSubtotal = subtotal * (quantity || 1);
    // Form MY: zon Jawa + had Rp30.000 tidak terpakai (free-ship) — COD sentiasa tersedia.
    const isMY = (form as any)?.addressCountry === 'MY';
    const codBlocked = !isMY && hook.addressData?.province ? isNoCodZoneProvince(hook.addressData.province) : false;
    // Mode dropdown+wizard: ongkir ASLI > maks → COD hilang (opsi sudah difilter),
    // tampilkan alasan agar customer tidak bingung.
    const codLimit = resolveCodShippingLimit(form?.paymentSettings?.cod);
    const codBlockedByShipping = !isMY && codLimit.enabled && isCodBlockedByShipping(shippingCost, form?.addressInputMode, codLimit.max, (hook as any)?.selectedEverproRate?.price ?? shippingCost);
    // Rute-lemah (aturan user 2026-10-17): tak ada kurir ≥80% → COD dikunci
    // (risiko retur tinggi). Ongkir tetap tampil di atas; bayar via non-COD.
    const codBlockedByLowPerf = !isMY && !!hook.lowPerfNoCod;
    const duitkuReady = useDuitkuReady(form?.brandId);

    const duitkuEnabled: string[] = (form?.paymentSettings?.duitku as any)?.methods || [];
    const duitkuMethods = DUITKU_FORM_METHODS.filter(m => duitkuEnabled.includes(m.value));

    // Ikut pola digital (preselect): grup duitku terpilih tanpa metode valid → pilih pertama.
    useEffect(() => {
        if (selectedPaymentKey === 'duitku' && !isKnownDuitkuMethod(selectedDuitkuMethod) && duitkuReady && duitkuMethods.length > 0) {
            setSelectedDuitkuMethod(duitkuMethods[0].value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPaymentKey, selectedDuitkuMethod, duitkuReady]);

    // Preselect PRIORITY (aturan user 2026-10-30): COD > Transfer Bank > QRIS >
    // Duitku QRIS > lainnya. sortedPaymentKeys sudah difilter blokir (zona/limit/
    // lowPerf), jadi pilih prioritas pertama yg tersedia — bukan sort[0] mentah.
    // Kasus wizard step 3: effectsForm sengaja undefined agar tak ada "Biaya COD"
    // menggantung di step 1 — tapi begitu section dirender, radio wajib checked
    // supaya klik pertama Selesaikan langsung submit (insiden food-life-3).
    useEffect(() => {
        if (!form || isSederhanaMode(form)) return;
        if (!selectedPaymentKey && sortedPaymentKeys.length > 0) {
            const keys = sortedPaymentKeys as string[];
            const cod = keys.find(k => k === 'cod' || k === 'codJawaBali' || k === 'codLuarJawaBali');
            const tf = keys.find(k => k.toLowerCase().includes('transfer'));
            const qris = keys.find(k => k.toLowerCase() === 'qris');
            const duitku = keys.find(k => k === 'duitku');
            setSelectedPaymentKey((cod || tf || qris || duitku || keys[0]) as any);
            if ((cod || tf || qris || keys[0]) === 'duitku' && !isKnownDuitkuMethod(selectedDuitkuMethod)) {
                const methods = ((form?.paymentSettings as any)?.duitku?.methods || []) as string[];
                const want = methods.includes('Duitku QRIS') ? 'Duitku QRIS' : methods[0];
                if (want) setSelectedDuitkuMethod(want as any);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPaymentKey, sortedPaymentKeys, form]);

    // Mode Sederhana (Form Sederhana 'none' non-Jawa & Mode Herbal 'simple'): TANPA
    // pilihan pembayaran — ongkir belum diketahui (dihitung sistem/CS) → jangan sampai
    // customer TF/QRIS sebelum ada ongkir. CS konfirmasi via chat.
    if (isSederhanaMode(form)) return null;

    return (
        <div className={blockSpacingClass}>
            <h3 className="font-semibold mb-2 text-slate-900 dark:text-slate-100">{ms(form, m => m.paymentMethod, 'Metode Pembayaran')}:</h3>
            {(codBlocked || codBlockedByShipping || codBlockedByLowPerf) && (
                <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3 py-2 mb-2">⚠️ COD tidak tersedia {codBlockedByLowPerf ? 'untuk rute ini (kurir di bawah standar, risiko retur tinggi)' : codBlocked ? `untuk ${hook.addressData.province}` : `untuk ongkir di atas ${codLimit.max.toLocaleString('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 })}`} — gunakan Transfer Bank/QRIS.</p>
            )}
            <div className="divide-y divide-slate-100 dark:divide-slate-700/50 border-y border-slate-100 dark:border-slate-700/50">{sortedPaymentKeys.map(key => {
                // Duitku SATUAN: 1 baris radio per metode (flat, gaya sama dengan baris lain).
                if (key === 'duitku') {
                    if (duitkuReady !== true || duitkuMethods.length === 0) return null;
                    return (<DuitkuRows
                        key="duitku" methods={duitkuMethods}
                        selectedPaymentKey={selectedPaymentKey} selectedDuitkuMethod={selectedDuitkuMethod}
                        setSelectedPaymentKey={setSelectedPaymentKey} setSelectedDuitkuMethod={setSelectedDuitkuMethod}
                        duitkuIconFor={duitkuIconFor}
                    />);
                }
                return (<StandardRow
                    key={key} payKey={key} form={form} isMY={isMY}
                    selectedPaymentKey={selectedPaymentKey}
                    setSelectedPaymentKey={setSelectedPaymentKey} setSelectedDuitkuMethod={setSelectedDuitkuMethod}
                    lineSubtotal={lineSubtotal} shippingCost={shippingCost}
                />);
            })}</div>
        </div>
    );
};
